import { ChevronDown } from 'lucide-react';
import type { RefObject } from 'react';

import { SearchInput } from '@/components/SearchInput';

type StatusOption = {
  value: string;
  label: string;
};

type MonthOption = {
  value: string;
  label: string;
};

type GroupOption = {
  value: string;
  label: string;
};

type ClaimsFiltersProps = {
  selectedMonths: string[];
  monthOptions: MonthOption[];
  showMonthDropdown: boolean;
  onToggleMonthDropdown: () => void;
  onSelectAllMonths: () => void;
  onToggleMonth: (monthValue: string) => void;
  monthDropdownRef: RefObject<HTMLDivElement | null>;

  selectedStatuses: string[];
  statusOptions: StatusOption[];
  showStatusDropdown: boolean;
  onToggleStatusDropdown: () => void;
  onSelectAllStatuses: () => void;
  onToggleStatus: (statusValue: string) => void;

  selectedGroups: string[];
  groupOptions: GroupOption[];
  showGroupDropdown: boolean;
  onToggleGroupDropdown: () => void;
  onSelectAllGroups: () => void;
  onToggleGroup: (groupValue: string) => void;

  dropdownRef: RefObject<HTMLDivElement | null>;
  groupDropdownRef: RefObject<HTMLDivElement | null>;

  nameQuery: string;
  onNameQueryChange: (value: string) => void;
};

const FLAG_DOT: Record<string, string> = {
  pending_yellow: 'bg-yellow-400',
  pending_red: 'bg-red-500',
};

export function ClaimsFilters({
  selectedMonths,
  monthOptions,
  showMonthDropdown,
  onToggleMonthDropdown,
  onSelectAllMonths,
  onToggleMonth,
  monthDropdownRef,
  selectedStatuses,
  statusOptions,
  showStatusDropdown,
  onToggleStatusDropdown,
  onSelectAllStatuses,
  onToggleStatus,
  selectedGroups,
  groupOptions,
  showGroupDropdown,
  onToggleGroupDropdown,
  onSelectAllGroups,
  onToggleGroup,
  dropdownRef,
  groupDropdownRef,
  nameQuery,
  onNameQueryChange,
}: ClaimsFiltersProps) {
  const monthButtonLabel =
    selectedMonths.length === monthOptions.length
      ? 'All Months'
      : `${selectedMonths.length} Selected`;

  return (
    <div className="mb-4 flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-end">
      {/* Name/email search */}
      <div className="flex flex-col gap-1 md:w-64">
        <span className="text-xs font-medium tracking-wider text-gray-400 uppercase">X-Teamer</span>
        <SearchInput
          value={nameQuery}
          onChange={onNameQueryChange}
          placeholder="Search by name or email"
        />
      </div>

      {/* Month filter */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium tracking-wider text-gray-400 uppercase">Month</span>
        <div className="relative shrink-0" ref={monthDropdownRef}>
          <button
            onClick={onToggleMonthDropdown}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50 focus:ring-1 focus:ring-red-500 focus:outline-hidden"
          >
            <span className="text-gray-700">{monthButtonLabel}</span>
            <ChevronDown size={16} className="text-gray-400" />
          </button>

          {showMonthDropdown && (
            <div className="absolute left-0 z-50 mt-2 w-56 rounded-md border border-gray-200 bg-white shadow-lg">
              <div className="p-3">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Filter by Month
                  </span>
                  <button
                    onClick={onSelectAllMonths}
                    className="text-xs font-medium text-red-600 hover:text-red-700"
                  >
                    {selectedMonths.length === monthOptions.length ? 'Clear All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {monthOptions.map((month) => (
                    <label
                      key={month.value}
                      className="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMonths.includes(month.value)}
                        onChange={() => onToggleMonth(month.value)}
                        className="size-4 cursor-pointer rounded border-gray-300 accent-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">{month.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status filter */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium tracking-wider text-gray-400 uppercase">Statuses</span>
        <div className="relative shrink-0" ref={dropdownRef}>
          <button
            onClick={onToggleStatusDropdown}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50 focus:ring-1 focus:ring-red-500 focus:outline-hidden"
          >
            <span className="text-gray-700">
              {selectedStatuses.length === statusOptions.length
                ? 'All Statuses'
                : `${selectedStatuses.length} Selected`}
            </span>
            <ChevronDown size={16} className="text-gray-400" />
          </button>

          {showStatusDropdown && (
            <div className="absolute left-0 z-50 mt-2 w-56 rounded-md border border-gray-200 bg-white shadow-lg md:right-0">
              <div className="p-3">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Filter Status
                  </span>
                  <button
                    onClick={onSelectAllStatuses}
                    className="text-xs font-medium text-red-600 hover:text-red-700"
                  >
                    {selectedStatuses.length > 0 ? 'Clear All' : 'Select All'}
                  </button>
                </div>
                <div>
                  {statusOptions.map((status) => (
                    <label
                      key={status.value}
                      className="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedStatuses.includes(status.value)}
                        onChange={() => onToggleStatus(status.value)}
                        className="size-4 cursor-pointer rounded border-gray-300 accent-red-600 focus:ring-red-500"
                      />
                      <span className="flex items-center gap-1.5 text-sm text-gray-700">
                        {status.label}
                        {FLAG_DOT[status.value] && (
                          <span
                            className={`size-2.5 shrink-0 rounded-xs ${FLAG_DOT[status.value]}`}
                          />
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Working Group filter */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium tracking-wider text-gray-400 uppercase">Group</span>
        <div className="relative shrink-0" ref={groupDropdownRef}>
          <button
            onClick={onToggleGroupDropdown}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50 focus:ring-1 focus:ring-red-500 focus:outline-hidden"
          >
            <span className="text-gray-700">
              {selectedGroups.length === groupOptions.length
                ? 'All Groups'
                : `${selectedGroups.length} Selected`}
            </span>
            <ChevronDown size={16} className="text-gray-400" />
          </button>

          {showGroupDropdown && (
            <div className="absolute left-0 z-50 mt-2 w-60 rounded-md border border-gray-200 bg-white shadow-lg md:right-0">
              <div className="p-3">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Filter Group
                  </span>
                  <button
                    onClick={onSelectAllGroups}
                    className="text-xs font-medium text-red-600 hover:text-red-700"
                  >
                    {selectedGroups.length === groupOptions.length ? 'Clear All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {groupOptions.map((group) => (
                    <label
                      key={group.value}
                      className="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedGroups.includes(group.value)}
                        onChange={() => onToggleGroup(group.value)}
                        className="size-4 cursor-pointer rounded border-gray-300 accent-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">{group.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
